/**
 * TimelineStorage mirrors UploadTimeline entries into IndexedDB so a page
 * reload doesn't lose diagnostic history. Persistence is best-effort — a
 * failed write must never break the upload.
 *
 * A separate database (clippn-observability) is used, not the uploader's
 * clippn-uploads DB. Rationale: observability schema changes must never
 * trigger an onupgradeneeded on the DB that owns critical resume state.
 *
 * Data layout:
 *   store `timelines` keyed by uploadId → {
 *     uploadId, updatedAt, entries: TimelineEntry[]
 *   }
 *
 * Writes are throttled to avoid pounding IDB while chunk_completed events
 * fire many times per second on fast uploads.
 */

const DB_NAME = "clippn-observability";
const DB_VERSION = 1;
const STORE = "timelines";
const WRITE_THROTTLE_MS = 2000;

const isSupported = () =>
  typeof indexedDB !== "undefined" && typeof window !== "undefined";

let dbPromise = null;

const openDb = () => {
  if (!isSupported()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "uploadId" });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[observability] IndexedDB unavailable", err);
    dbPromise = null;
    return null;
  });
  return dbPromise;
};

const withStore = async (mode, fn) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

const asPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

/**
 * Attach persistence to a UploadTimeline instance. Subscribes to appends,
 * throttles writes, returns a disposer.
 *
 * @param {import('./UploadTimeline').UploadTimeline} timeline
 * @returns {() => void} unsubscribe (does NOT delete the stored record)
 */
export const attachTimelinePersistence = (timeline) => {
  if (!timeline || !isSupported()) return () => {};

  let pendingWrite = null;
  let lastWriteAt = 0;
  let disposed = false;

  const flush = async () => {
    if (disposed) return;
    pendingWrite = null;
    lastWriteAt = Date.now();
    try {
      await withStore("readwrite", (store) =>
        asPromise(
          store.put({
            uploadId: timeline.uploadId,
            updatedAt: lastWriteAt,
            entries: timeline.toArray(),
          })
        )
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[observability] timeline persist failed", err);
    }
  };

  const scheduleFlush = () => {
    if (disposed) return;
    if (pendingWrite) return;
    const elapsed = Date.now() - lastWriteAt;
    const delay = Math.max(0, WRITE_THROTTLE_MS - elapsed);
    pendingWrite = setTimeout(flush, delay);
  };

  const unsubscribe = timeline.subscribe(scheduleFlush);

  return () => {
    disposed = true;
    unsubscribe();
    if (pendingWrite) {
      clearTimeout(pendingWrite);
      pendingWrite = null;
    }
  };
};

/**
 * Load a persisted timeline for an uploadId (used on page reload).
 * @param {string} uploadId
 * @returns {Promise<Array<import('./UploadEvents').TimelineEntry>>}
 */
export const loadTimelineEntries = async (uploadId) => {
  if (!uploadId) return [];
  try {
    const record = await withStore("readonly", (store) =>
      asPromise(store.get(uploadId))
    );
    return record?.entries || [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[observability] timeline load failed", err);
    return [];
  }
};

/**
 * Fetch every stored timeline. Used by the admin dashboard's local diagnostic
 * export tools and by boot-time cleanup.
 */
export const listStoredTimelines = async () => {
  try {
    return (
      (await withStore("readonly", (store) =>
        new Promise((resolve, reject) => {
          const req = store.getAll();
          req.onsuccess = () => resolve(req.result || []);
          req.onerror = () => reject(req.error);
        })
      )) || []
    );
  } catch (_err) {
    return [];
  }
};

/**
 * Remove one uploadId's persisted timeline. Called after successful upload
 * completion, admin cleanup, or a manual "clear diagnostics" action.
 */
export const removeStoredTimeline = async (uploadId) => {
  if (!uploadId) return;
  try {
    await withStore("readwrite", (store) => asPromise(store.delete(uploadId)));
  } catch (_err) {
    /* best effort */
  }
};

/**
 * Housekeeping: drop timelines older than the retention window. Called on
 * boot to bound total disk usage — otherwise abandoned uploads pile up.
 *
 * @param {number} [maxAgeMs] default 7 days
 */
export const pruneStoredTimelines = async (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
  try {
    const records = await listStoredTimelines();
    const cutoff = Date.now() - maxAgeMs;
    await Promise.all(
      records
        .filter((r) => (r.updatedAt || 0) < cutoff)
        .map((r) => removeStoredTimeline(r.uploadId))
    );
  } catch (_err) {
    /* best effort */
  }
};
